import {useAtom} from 'jotai';
import {useEffect} from 'react';

import {ColumnElement, columnState} from './columnState';
import {ETableColumn} from './types';

export const getSelectionAndExpanderKeys = (): string[] => [
  '_selection',
  '_expander',
];

export const useColumnsConfig = ({
  userColumns,
  persistenceId,
}: {
  userColumns: ETableColumn<string, unknown>[];
  persistenceId?: string;
}): {
  initialHiddenColumns: string[];
  initialColumnOrder: string[];
  setColumnsStateByTableId: (
    state:
      | ((
          currentState: Record<string, ColumnElement[]>
        ) => Record<string, ColumnElement[]>)
      | Record<string, ColumnElement[]>
  ) => void;
  savedColumnConfig: ColumnElement[];
} => {
  const [columnsStateByTableId, setColumnsStateByTableId] =
    useAtom(columnState);

  function updateStoredColumConfig(columnConfig: ColumnElement[]) {
    if (persistenceId) {
      setColumnsStateByTableId((storedConfigs) => ({
        ...storedConfigs,
        [persistenceId]: columnConfig,
      }));
    }
  }

  function getStoredColumnConfig(): {
    columnConfig: ColumnElement[];
    pendingUpdate: boolean;
  } {
    const defaultColumnConfig = userColumns.map((column) => ({
      name: column.name,
      active: !column.defaultHidden,
      defaultHidden: Boolean(column.defaultHidden),
    }));

    if (!persistenceId) {
      return {columnConfig: defaultColumnConfig, pendingUpdate: false};
    }

    const storedColumnConfig = columnsStateByTableId[persistenceId];
    if (!storedColumnConfig) {
      return {
        columnConfig: defaultColumnConfig,
        pendingUpdate: true,
      };
    }

    const columnConfig: ColumnElement[] = [];

    // Only add columns from stored columns that are still available
    storedColumnConfig.forEach((column) => {
      const matchingDefaultColumnConfig = defaultColumnConfig.find(
        ({name, defaultHidden}) =>
          name === column.name && defaultHidden === column.defaultHidden
      );
      if (matchingDefaultColumnConfig) {
        columnConfig.push(column);
      }
    });

    let isModified = columnConfig.length !== storedColumnConfig.length;

    // Add newly added columns at its index
    defaultColumnConfig.forEach((column, index) => {
      if (!columnConfig.some(({name}) => name === column.name)) {
        columnConfig.splice(index, 0, column);
        isModified = true;
      }
    });

    return {columnConfig, pendingUpdate: isModified};
  }

  const {columnConfig: storedColumnConfig, pendingUpdate} =
    getStoredColumnConfig();

  useEffect(() => {
    if (pendingUpdate) {
      updateStoredColumConfig(storedColumnConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistenceId]);

  const initialColumnOrder = [
    ...getSelectionAndExpanderKeys(),
    ...storedColumnConfig.map(({name}) => name),
  ];

  const initialHiddenColumns = storedColumnConfig
    .filter((column) => !column.active)
    .map((column) => column.name);

  return {
    initialHiddenColumns,
    initialColumnOrder,
    setColumnsStateByTableId,
    savedColumnConfig: storedColumnConfig,
  };
};
